import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Send, X, Loader2, AlertCircle, Paperclip, File, Trash2 } from 'lucide-react';
import { useSendPatientMessage } from '@/hooks/usePatientMessages';
import { 
  validateMessageAttachmentFile,
  useAttachMessageDocument,
} from '@/hooks/useMessageAttachments';
import { useUploadDocument } from '@/hooks/useDocuments';
import { Button } from '@/components/ui/Button';
import { sendPatientMessageCompose } from '@/lib/sendPatientMessageCompose';

export interface MessageModalProps {
  patientId: string;
  patientName: string;
  clinicId: string;
  onClose: () => void;
}

export function MessageModal({
  patientId,
  patientName,
  onClose,
}: MessageModalProps) {
  const { t } = useTranslation();
  const sendMessage = useSendPatientMessage();
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachMessageDocument(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    
    for (const file of files) {
      const validationError = validateMessageAttachmentFile(file);
      if (validationError) {
        if (validationError === 'size_limit_exceeded') {
          toast.error(t('documents.errors.size_limit', `Filen ${file.name} er for stor (max 50 MB)`));
        } else if (validationError === 'invalid_type') {
          toast.error(t('documents.errors.invalid_type', `Filtypen for ${file.name} er ikke tilladt`));
        }
      } else {
        validFiles.push(file);
      }
    }
    
    setSelectedFiles((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedMessage = message.trim();
    if (!trimmedMessage && selectedFiles.length === 0) {
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      await sendPatientMessageCompose({
        patientId,
        content: trimmedMessage,
        files: selectedFiles,
        sendMessage: (params) => sendMessage.mutateAsync(params),
        uploadDocument: (params) => uploadDocument.mutateAsync(params),
        attachDocument: (params) => attachDocument.mutateAsync(params),
      });

      toast.success(t('messages.toasts.sent', 'Besked sendt'));
      onClose();
    } catch (err) {
      console.error('Error sending message:', err);
      setError(t('messages.errors.send_failed', 'Kunne ikke sende besked'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-ds-scrim p-4">
      <div className="mx-auto mt-10 max-w-2xl rounded-xl bg-ds-card shadow-[var(--sh-md)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ds-line px-6 py-4">
          <h3 className="text-lg font-medium text-ds-text">
            {t('messages.modal.title', 'Send besked')}
          </h3>
          <Button type="button" variant="tertiary" size="icon" onClick={onClose} aria-label={t('common.close', 'Luk')}>
            <X />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 rounded-lg bg-ds-red-tint p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-ds-red" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-ds-red-ink">
                    {error}
                  </h3>
                </div>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-ds-text">
              {t('messages.modal.recipient', 'Modtager')}
            </label>
            <div className="rounded-md bg-ds-sunk px-3 py-2 text-sm text-ds-text">
              {patientName}
            </div>
          </div>

          <div className="mb-4">
            <label
              htmlFor="message"
              className="mb-2 block text-sm font-medium text-ds-text"
            >
              {t('messages.modal.message', 'Besked')}
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              placeholder={t('messages.modal.placeholder', 'Skriv din besked her...')}
              className="ds-field block w-full"
              disabled={isUploading}
            />
          </div>

          <div className="mb-6">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
              className="hidden"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <Paperclip />
              {t('messages.modal.attach_file', 'Vedhæft fil')}
            </Button>
            
            {selectedFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-md border border-ds-line bg-ds-sunk px-3 py-2"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <File className="h-4 w-4 flex-shrink-0 text-ds-muted" />
                      <span className="truncate text-sm text-ds-text">
                        {file.name}
                      </span>
                      <span className="flex-shrink-0 text-xs text-ds-muted">
                        ({Math.round(file.size / 1024)} KB)
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="danger"
                      size="icon"
                      className="ml-2 shrink-0"
                      onClick={() => handleRemoveFile(index)}
                      disabled={isUploading}
                      aria-label={t('common.delete')}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isUploading}
            >
              {t('common.cancel', 'Annuller')}
            </Button>
            <Button
              type="submit"
              disabled={isUploading || (!message.trim() && selectedFiles.length === 0)}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('messages.modal.sending', 'Sender...')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {t('messages.modal.send', 'Send')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
